import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { db } from "../../config/firebaseConfig";
import { ref, get } from "firebase/database";

interface ImprintSection {
  title: string;
  content: string;
  listItems?: string[];
}

interface ImprintData {
  introduction: string;
  sections: ImprintSection[];
}

const ImprintSkeleton: React.FC = () => {
  return (
    <div className="animate-pulse p-4 md:px-[15vw] md:pt-[5vh] w-full flex flex-col font-normal text-sm space-y-5">
      {/* Title skeleton */}
      <div className="mt-[4%]">
        <div className="h-12 bg-gray-200 rounded-md w-3/4"></div>
      </div>

      {/* Introduction skeleton */}
      <div className="h-4 bg-gray-200 rounded-md w-full"></div>
      <div className="h-4 bg-gray-200 rounded-md w-5/6"></div>

      {/* Sections skeleton */}
      <div className="space-y-6 px-4">
        {[1, 2, 3, 4, 5].map((_, index) => (
          <div key={index} className="space-y-3">
            {/* Section title */}
            <div className="h-5 bg-gray-200 rounded-md w-1/3"></div>
            {/* Section content */}
            <div className="h-4 bg-gray-200 rounded-md w-full"></div>
            <div className="h-4 bg-gray-200 rounded-md w-11/12"></div>
            {/* List items (for first section) */}
            {index === 0 && (
              <div className="space-y-2 px-4">
                <div className="h-4 bg-gray-200 rounded-md w-2/3"></div>
                <div className="h-4 bg-gray-200 rounded-md w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded-md w-1/2"></div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ImprintDataProtection: React.FC = () => {
  const [imprintData, setImprintData] = useState<ImprintData>({
    introduction: "",
    sections: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const aboutElement = document.getElementById("topbartopscroll");
    if (aboutElement) {
      aboutElement.scrollIntoView({ behavior: "smooth" });
    }

    // Fetch imprint data from Firebase
    const fetchImprintData = async () => {
      try {
        const imprintRef = ref(db, "LayoutSettingData/ImprintData");
        const snapshot = await get(imprintRef);

        if (snapshot.exists()) {
          setImprintData(snapshot.val());
        } else {
          // If no data exists, use default content
          const defaultData: ImprintData = {
            introduction:
              "At Afrocart, your privacy and the protection of your data are of utmost importance. This section provides key information about our company, how we manage your data, and your rights under data protection laws.",
            sections: [
              {
                title: "Company Information",
                content: "",
                listItems: [
                  "Company Name: Afrocart Inc.",
                  "Customer Support: +1289-460-1541",
                  "Inquiries: admin@afrocart.ca",
                ],
              },
              {
                title: "Data Protection Officer (DPO)",
                content:
                  "If you have any concerns or questions related to data privacy or how your personal information is handled, please contact our Data Protection Officer at dpo@afrocart.ca.",
              },
              {
                title: "Data Usage and Privacy",
                content:
                  "We are committed to using your personal information only in compliance with relevant legal standards. This includes processing data for improving our services, facilitating transactions, and enhancing your overall experience on our platform. For more detailed insights into our data practices, please review our Privacy Policy.",
              },
              {
                title: "Your Rights Under Data Protection Laws",
                content:
                  "You have the right to access, correct, or delete your personal data at any time. Additionally, you can object to how your data is processed or request limitations on its use. To exercise these rights or if you need further details, please reach out to our Data Protection Officer or refer to our Privacy Policy.",
              },
              {
                title: "Legal Updates",
                content:
                  "We regularly review and update our data protection practices to ensure that they remain in full compliance with evolving laws and regulations. Please check back here for the latest updates on how we handle your personal information.",
              },
            ],
          };
          setImprintData(defaultData);
        }
      } catch (error) {
        console.error("Error fetching imprint data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchImprintData();
  }, []);

  if (loading) {
    return <ImprintSkeleton />;
  }

  // Helper function to process email links in content
  const renderContent = (content: string) => {
    // Check if content contains email addresses
    if (content.includes("@")) {
      const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/g;
      const parts = content.split(emailRegex);

      return (
        <>
          {parts.map((part, index) => {
            if (part.match(emailRegex)) {
              return (
                <a
                  key={index}
                  href={`mailto:${part}`}
                  className="font-helveticaMedium text-customGreenColor opacity-50"
                >
                  {part}
                </a>
              );
            }
            return part;
          })}
        </>
      );
    }

    return content;
  };

  // Helper function to process email links in list items
  const renderListItem = (item: string, index: number) => {
    if (item.includes("@")) {
      const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/g;
      const parts = item.split(emailRegex);

      return (
        <li key={index}>
          {parts.map((part, partIndex) => {
            if (part.match(emailRegex)) {
              return (
                <a
                  key={partIndex}
                  href={`mailto:${part}`}
                  className="font-helveticaMedium text-customGreenColor opacity-50"
                >
                  {part}
                </a>
              );
            }
            return part;
          })}
        </li>
      );
    }

    return <li key={index}>{item}</li>;
  };

  return (
    <div>
      <div className="p-4 md:px-[15vw] md:pt-[5vh] w-full flex flex-col font-normal text-sm space-y-5 text-black">
        <div>
          {/* <p className="flex items-start text-[1.65vh] text-customGrayBase">
            Last updated: 10/22/2024
          </p> */}
          <h1 className="mt-[4%] font-helveticaBold text-[5.5vh] xxl:text-[7.5vh] leading-none text-black">
            Imprint & Data Protection
          </h1>
        </div>
        <p>{imprintData.introduction}</p>
        <ol className="decimal_ol space-y-4 px-4">
          {imprintData.sections.map((section, index) => (
            <li key={index} className="font-helveticaBold text-black">
              {section.title}
              <p className="font-helvetica text-black">
                {renderContent(section.content)}
              </p>
              {section.listItems && section.listItems.length > 0 && (
                <ul className="font-helvetica disc_li space-y-2 px-4">
                  {section.listItems.map((item, itemIndex) =>
                    renderListItem(item, itemIndex)
                  )}
                </ul>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default ImprintDataProtection;
